import React from 'react'
import { ButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap'
import { localized } from '@/decorators/localized'
import context from '@/context'
import { Button } from '@/components/button'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { openDialog } from '@/store/Dialogs'
import { dialogTypes } from '@/dialogs'
import { actionCreators } from '@/store/ReportSubmissionGroup'
import { reportSubmissionGroupExportType } from '@/enums/reportSubmissionGroupExportType'
import { downloadFile } from '@/utils/downloader'
import Resx from '@/components/Resx'
import { xsdSchemaTypes } from '@/enums/xsdSchemaTypes'
import { withPermissions } from '@/decorators/withPermissions'
import { ReportGroupProcessState } from '@/enums/reportGroupProcessState'

const fileFormats = {
  excel: 0,
  xml: 1
}
@localized()
@withPermissions()
class ReportDownloadButtons extends React.Component {
  static propTypes = {
    downloadXlsxEmptyVisible: PropTypes.bool,
    downloadXlsxPreviousVisible: PropTypes.bool,
    downloadXlsxCurrentVisible: PropTypes.bool,
    downloadXmlEmptyVisible: PropTypes.bool,
    downloadXmlPreviousVisible: PropTypes.bool,
    downloadXmlCurrentVisible: PropTypes.bool,
    localize: PropTypes.any,
    setInstanceGroupState: PropTypes.func,
    openDialog: PropTypes.func,
    isGranted: PropTypes.func,
    reportSubmissionGroup: PropTypes.any,
    previousSubmissionGroupState: PropTypes.number
  };

  static defaultProps = {
    downloadXlsxEmptyVisible: true,
    downloadXlsxPreviousVisible: true,
    downloadXlsxCurrentVisible: true,
    downloadXmlEmptyVisible: true,
    downloadXmlPreviousVisible: true,
    downloadXmlCurrentVisible: true
  };

  constructor (props, context) {
    super(props, context)

    this.state = {
      downloadXlsOpen: false,
      downloadXmlOpen: false
    }
  }

  runCheckingRules = () => {
    const { reportSubmissionGroup, setInstanceGroupState } = this.props

    setInstanceGroupState(ReportGroupProcessState.enum.CheckingRulesRunning)
    context.api.reportSubmissionService.runCheckingRules(reportSubmissionGroup.id)
  }

  displayError = () => {
    this.props.openDialog({
      dialogType: dialogTypes.alert,
      title: 'Reporting.GeneratingExcel',
      content: 'Reporting.GeneratingExcelError'
    })
  }

  confirmDialog = (action) => {
    this.props.openDialog({
      dialogType: dialogTypes.confirm,
      title: 'Reporting.GenerateExcel',
      content: 'Reporting.ConfirmGenerateExcel',
      onAction: action
    })
  }

  onDownloadError = () => {
    this.props.openDialog({
      dialogType: dialogTypes.alert,
      title: 'Reporting.GenerateExcel',
      content: 'Reporting.ExportDownloadError'
    })
  }

  downloadArabicEmptyXlsx = () => { this.downloadEmptyXlsx('ar') }
  downloadEnglishEmptyXlsx = () => { this.downloadEmptyXlsx('en') }
  downloadEmptyXlsx = (languageName) => {
    this.downloadXlsx(
      languageName,
      reportSubmissionGroupExportType.enum.Clean,
      false
    )
  }
  downloadArabicPreviousDataXlsx = () => { this.downloadDataXlsx(reportSubmissionGroupExportType.enum.PreviousSubmission, 'ar') }
  downloadEnglishPreviousDataXlsx = () => { this.downloadDataXlsx(reportSubmissionGroupExportType.enum.PreviousSubmission, 'en') }
  downloadArabicCurrentDataXlsx = () => { this.downloadDataXlsx(reportSubmissionGroupExportType.enum.CurrentSubmission, 'ar') }
  downloadEnglishCurrentDataXlsx = () => { this.downloadDataXlsx(reportSubmissionGroupExportType.enum.CurrentSubmission, 'en') }
  downloadDataXlsx = (exportType, languageName) => {
    if (this.props.reportSubmissionGroup.state === 4) {
      this.downloadXlsx(
        languageName,
        exportType,
        true
      )
    } else {
      // start export job
      this.confirmDialog(
        () => context.api.reportExportService.exportReportSubmissionGroupAsExcelFile({ Id: this.props.reportSubmissionGroup.id, ExportType: exportType, EnglishTranslation: languageName === 'en' })
      )
    }
  }

  downloadXlsx = (languageName, exportType, isAccepted) => {
    const fileAttachmentId = this.findFileAttachment(languageName, exportType, fileFormats.excel, isAccepted)
    if (fileAttachmentId) {
      const input = {
        reportSubmissionGroupId: this.props.reportSubmissionGroup.id,
        fileAttachmentId
      }
      downloadFile(input, context.api.reportSubmissionGroupService.downloadExport, null, null, this.onDownloadError)
    }
  }

  downloadEmptyXml = () => { this.downloadXml(reportSubmissionGroupExportType.enum.Clean, false) }

  downloadCurrentDataXml = () => { this.downloadDataXml(reportSubmissionGroupExportType.enum.CurrentSubmission) }

  downloadPreviousDataXml = () => { this.generateXml(reportSubmissionGroupExportType.enum.PreviousSubmission) }

  downloadDataXml = (exportType) => {
    if (this.props.reportSubmissionGroup.state === 4) {
      this.downloadXml(
        exportType,
        true
      )
    } else {
      // start export job
      this.generateXml(exportType)
    }
  }

  generateXml = (exportType) => {
    const id = this.props.reportSubmissionGroup.id
    this.confirmDialog(() => context.api.reportExportService.exportReportSubmissionGroupAsXmlFile({ Id: id, ExportType: exportType }))
  }

  downloadXml = (exportType, isAccepted) => {
    const fileAttachmentId = this.findFileAttachment('en', exportType, fileFormats.xml, isAccepted)
    if (fileAttachmentId) {
      const input = {
        reportSubmissionGroupId: this.props.reportSubmissionGroup.id,
        fileAttachmentId
      }
      downloadFile(input, context.api.reportSubmissionGroupService.downloadExport, null, null, this.onDownloadError)
    }
  }

  downloadXsd = () => {
    downloadFile(xsdSchemaTypes.enum.ReportSubmissionGroupXml, context.api.reportExportService.downloadXsdSchema, null, null, this.onDownloadError)
  }

  findFileAttachment = (languageName, exportType, exportFormat, isAccepted) => {
    const { exportDownloads } = this.props.reportSubmissionGroup
    if (exportDownloads && exportDownloads.length > 0) {
      const exportedFile = exportDownloads.find(x =>
        x.languageName === languageName &&
        x.exportType === exportType &&
        x.exportFormat === exportFormat &&
        x.isAccepted === isAccepted
      )
      if (exportedFile) { return exportedFile.fileAttachmentId }
    }
    return null
  }
  hasExport = (languageName, exportType, exportFormat, isAccepted) => {
    return this.findFileAttachment(languageName, exportType, exportFormat, isAccepted) !== null
  }

  render () {
    const { reportSubmissionGroup, localize, previousSubmissionGroupState } = this.props

    if (!reportSubmissionGroup) { return null }
    const { previousReportSubmissionGroupId } = reportSubmissionGroup
    const currentDataEnabled = reportSubmissionGroup.processState === 0 || reportSubmissionGroup.state === 4
    const downloadPreviousEnabled = previousSubmissionGroupState === 0
    return (
      <div className='DownloadButtons button-section-alternative'>
        <Button className='btn btn-md'
          disabled={reportSubmissionGroup.highestSeverity === null || reportSubmissionGroup.processState !== 0}
          onClick={this.runCheckingRules}>
          <Resx name='Reporting.RunCheckingRules' />
        </Button>
        <ButtonDropdown isOpen={this.state.downloadXlsOpen} toggle={() => this.setState({ downloadXlsOpen: !this.state.downloadXlsOpen })}>
          <DropdownToggle caret>
            {localize('Reporting.DownloadXls')}
          </DropdownToggle>
          <DropdownMenu>
            {this.props.downloadXlsxEmptyVisible &&
              <React.Fragment>
                <DropdownItem
                  disabled={!this.hasExport('en', reportSubmissionGroupExportType.enum.Clean, fileFormats.excel, false)}
                  onClick={this.downloadEnglishEmptyXlsx}>{localize('Reporting.EmptyTemplateEnglish')}</DropdownItem>
                <DropdownItem
                  disabled={!this.hasExport('ar', reportSubmissionGroupExportType.enum.Clean, fileFormats.excel, false)}
                  onClick={this.downloadArabicEmptyXlsx}>{localize('Reporting.EmptyTemplateArab')}</DropdownItem>
              </React.Fragment>
            }
            {this.props.downloadXlsxPreviousVisible &&
              <React.Fragment>
                <DropdownItem
                  disabled={!previousReportSubmissionGroupId || !downloadPreviousEnabled}
                  onClick={this.downloadEnglishPreviousDataXlsx}>{localize('Reporting.PreviousCycleSubmissionEnglish')}</DropdownItem>
                <DropdownItem
                  disabled={!previousReportSubmissionGroupId || !downloadPreviousEnabled}
                  onClick={this.downloadArabicPreviousDataXlsx}>{localize('Reporting.PreviousCycleSubmissionArab')}</DropdownItem>
              </React.Fragment>
            }
            {this.props.downloadXlsxCurrentVisible &&
              <React.Fragment>
                <DropdownItem
                  disabled={!currentDataEnabled}
                  onClick={this.downloadEnglishCurrentDataXlsx}>
                  {localize('Reporting.CurrentDataEnglish')}
                </DropdownItem>
                <DropdownItem
                  disabled={!currentDataEnabled}
                  onClick={this.downloadArabicCurrentDataXlsx}>
                  {localize('Reporting.CurrentDataArab')}
                </DropdownItem>
              </React.Fragment>
            }
          </DropdownMenu>
        </ButtonDropdown>
        <ButtonDropdown isOpen={this.state.downloadXmlOpen} toggle={() => this.setState({ downloadXmlOpen: !this.state.downloadXmlOpen })}>
          <DropdownToggle caret>
            {localize('Reporting.DownloadXml')}
          </DropdownToggle>
          <DropdownMenu>
            {this.props.downloadXmlEmptyVisible &&
              <DropdownItem
                disabled={!this.hasExport('en', reportSubmissionGroupExportType.enum.Clean, fileFormats.xml, false)}
                onClick={this.downloadEmptyXml}>{localize('Reporting.EmptyTemplate')}</DropdownItem> }
            {this.props.downloadXmlPreviousVisible &&
              <DropdownItem disabled={!this.props.reportSubmissionGroup.previousReportSubmissionGroupId || !downloadPreviousEnabled} onClick={this.downloadPreviousDataXml}>{localize('Reporting.PreviousCycleSubmission')}</DropdownItem>
            }
            {this.props.downloadXmlCurrentVisible && <DropdownItem
              disabled={!currentDataEnabled}
              onClick={this.downloadCurrentDataXml}>
              {localize('Reporting.CurrentData')}
            </DropdownItem> }
          </DropdownMenu>
        </ButtonDropdown>
        <Button icon='save' disabled={!this.props.isGranted(['CanViewReports', 'CanViewReportSubmissions'])} onClick={this.downloadXsd}>{localize('Reporting.DownloadXsd')}</Button>
      </div>
    )
  }
}

const mapDispatchToProps = {
  openDialog,
  setInstanceGroupState: actionCreators.setInstanceGroupState
}

export default connect(null, mapDispatchToProps)(ReportDownloadButtons)



// WEBPACK FOOTER //
// ./src/components/ReportDownloadButtons.js